import { useState } from 'react';
import { Tag } from 'primereact/tag';
import { Panel } from 'primereact/panel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { useQuery } from '@tanstack/react-query';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { ProcessedTraceLog } from '../../models/tracing';
import { getFileContent } from '../../services/feeTracingService';
import { toLocalDateTimePrecise } from '../../utils/date-helper';
import { formatXml } from '../../utils/xmlFormatter';
import styles from './TraceLogDetailPanel.module.css';

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Button
      icon={copied ? 'pi pi-check' : 'pi pi-copy'}
      label={copied ? 'Copied!' : 'Copy'}
      className="p-button-text p-button-sm"
      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', color: copied ? '#76b729' : '#8aabc8' }}
      onClick={handleCopy}
    />
  );
}

interface TraceLogDetailPanelProps {
  log: ProcessedTraceLog;
}

function FormattedXmlDisplay({ xml }: { xml: string }) {
  const formatted = formatXml(xml);
  return (
    <div className="rounded border-1 border-gray-200 overflow-hidden max-h-[48rem]" style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', top: '0.4rem', right: '0.5rem', zIndex: 10 }}>
        <CopyButton text={formatted} />
      </div>
      <SyntaxHighlighter
        language="markup"
        style={nord}
        customStyle={{
          margin: 0,
          padding: '1rem',
          fontSize: '0.875rem',
          maxHeight: '48rem',
        }}
        showLineNumbers={formatted.split('\n').length > 5}
        wrapLongLines
      >
        {formatted}
      </SyntaxHighlighter>
    </div>
  );
}

function JsonDisplay({ parsed }: { parsed: unknown }) {
  const text = JSON.stringify(parsed, null, 2);
  return (
    <div className="rounded border-1 border-gray-200 overflow-hidden max-h-[48rem]" style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', top: '0.4rem', right: '0.5rem', zIndex: 10 }}>
        <CopyButton text={text} />
      </div>
      <SyntaxHighlighter
        language="json"
        style={nord}
        customStyle={{ margin: 0, padding: '1rem', fontSize: '0.875rem', maxHeight: '48rem' }}
        showLineNumbers
        wrapLongLines
      >
        {text}
      </SyntaxHighlighter>
    </div>
  );
}

const parseEventData = (raw: string): { parsed: any; type: 'json' | 'xml' | 'text' } => {
  try {
    return { parsed: JSON.parse(raw), type: 'json' };
  } catch {
    if (raw.trim().startsWith('<')) {
      return { parsed: raw, type: 'xml' };
    }
    return { parsed: raw, type: 'text' };
  }
};

const TraceLogDetailPanel = ({ log }: TraceLogDetailPanelProps) => {
  console.log('log', log);
  const fileQuery = useQuery<string>({
    queryKey: ['file-content', log.eventDataFileId],
    queryFn: () => getFileContent(log.eventDataFileId!),
    enabled: !!log.eventDataFileId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const formatDateTime = (timestamp: string) => {
    return toLocalDateTimePrecise(timestamp) || timestamp;
  };

  const eventData = fileQuery.data ? parseEventData(fileQuery.data) : null;

  return (
    <div className="trace-log-detail-panel flex flex-column gap-4">
      <Panel header="Event Information" className="w-full">
        <div className="flex flex-column gap-3">
          <div>
            <div className="font-semibold text-sm text-gray-600 mb-1">Description</div>
            <div className="text-base">{log.description || 'N/A'}</div>
          </div>
          
          <div>
            <div className="font-semibold text-sm text-gray-600 mb-1">Timestamp</div>
            <div className="text-base font-mono">{formatDateTime(log.eventTimestamp)}</div>
          </div>

          {log.messageType && log.messageType !== 'None' && (
            <div>
              <div className="font-semibold text-sm text-gray-600 mb-1">Message Type</div>
              <div className="text-base">
                <Tag
                  value={log.messageType}
                  severity={log.messageType === 'Request' ? 'info' : 'success'}
                />
              </div>
            </div>
          )}

          {log.vendor && (
            <div>
              <div className="font-semibold text-sm text-gray-600 mb-1">Vendor</div>
              <div className="text-base">{log.vendor}</div>
            </div>
          )}

          {log.transactionName && (
            <div>
              <div className="font-semibold text-sm text-gray-600 mb-1">Transaction Name</div>
              <div className="text-base">{log.transactionName}</div>
            </div>
          )}

          {log.method && (
            <div>
              <div className="font-semibold text-sm text-gray-600 mb-1">Method</div>
              <div className="text-base font-mono">{log.method}</div>
            </div>
          )}

          {log.uri && (
            <div>
              <div className="font-semibold text-sm text-gray-600 mb-1">URI</div>
              <div className="text-sm font-mono bg-gray-100 p-2 rounded overflow-auto">{log.uri}</div>
            </div>
          )}

          {log.responseStatus != null && (
            <div>
              <div className="font-semibold text-sm text-gray-600 mb-1">Response Status</div>
              <div className="text-base">
                <Tag
                  value={String(log.responseStatus)}
                  severity={log.responseStatus >= 200 && log.responseStatus < 300 ? 'success' : log.responseStatus >= 400 ? 'danger' : 'warning'}
                />
              </div>
            </div>
          )}

          {log.durationMs != null && (
            <div>
              <div className="font-semibold text-sm text-gray-600 mb-1">Duration</div>
              <div className="text-base font-mono">{log.durationMs} ms</div>
            </div>
          )}

          {log.loanNumber && (
            <div>
              <div className="font-semibold text-sm text-gray-600 mb-1">Loan Number</div>
              <div className="text-base">{log.loanNumber}</div>
            </div>
          )}

          {log.loanIdentifier && (
            <div>
              <div className="font-semibold text-sm text-gray-600 mb-1">Loan Identifier</div>
              <div className="text-base">{log.loanIdentifier}</div>
            </div>
          )}

          {log.traceGroup && (
            <div>
              <div className="font-semibold text-sm text-gray-600 mb-1">Trace Group</div>
              <div className="text-sm font-mono bg-gray-100 p-2 rounded">{log.traceGroup}</div>
            </div>
          )}

          {log.traceGroupDescription && (
            <div>
              <div className="font-semibold text-sm text-gray-600 mb-1">Group Description</div>
              <div className="text-base">{log.traceGroupDescription}</div>
            </div>
          )}
        </div>
      </Panel>

      {log.contextProperties && (
        <Panel header="Context Properties" toggleable collapsed={false} className="w-full">
          {log.parsedContextProperties ? (
            <div className="flex flex-column gap-2">
              {Object.entries(log.parsedContextProperties).map(([key, value]) => {
                let displayValue: string;
                if (typeof value === 'object') {
                  displayValue = JSON.stringify(value, null, 2);
                } else {
                  const stringValue = String(value);
                  displayValue = stringValue.replace(/,(?!\s)/g, ', ');
                }
                
                return (
                  <div key={key} className="flex flex-column gap-1">
                    <div className="font-semibold text-sm text-primary">{key}</div>
                    <div className={`bg-gray-100 p-2 rounded font-mono text-sm ${styles.contextPropertyValue}`}>
                      {displayValue}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <pre className={`bg-gray-100 p-3 rounded overflow-auto text-sm font-mono m-0 ${styles.contextPropertiesRaw}`}>
              {log.contextProperties}
            </pre>
          )}
        </Panel>
      )}

      {log.eventDataFileId && (
        <Panel 
          header={
            <div className="flex align-items-center gap-2">
              <span>Event Data</span>
              {eventData && (
                <Tag 
                  value={eventData.type.toUpperCase()} 
                  severity={eventData.type === 'json' ? 'info' : eventData.type === 'xml' ? 'warning' : 'secondary'} 
                />
              )}
            </div>
          } 
          toggleable 
          collapsed={false} 
          className="w-full"
        >
          {fileQuery.isLoading && (
            <div className="flex align-items-center justify-content-center gap-2 py-4">
              <ProgressSpinner style={{ width: '24px', height: '24px' }} strokeWidth="4" />
              <span className="text-sm text-gray-500">Loading event data...</span>
            </div>
          )}
          {fileQuery.isError && (
            <div className="text-red-500 text-sm p-3">
              <i className="pi pi-exclamation-triangle mr-2" />
              Failed to load event data: {fileQuery.error instanceof Error ? fileQuery.error.message : 'Unknown error'}
            </div>
          )}
          {eventData && (
            eventData.type === 'json' ? (
              <JsonDisplay parsed={eventData.parsed} />
            ) : eventData.type === 'xml' ? (
              <FormattedXmlDisplay xml={eventData.parsed} />
            ) : (
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', top: '0.4rem', right: '0.5rem', zIndex: 10 }}>
                  <CopyButton text={String(eventData.parsed)} />
                </div>
                <pre className="bg-gray-50 p-3 rounded overflow-auto max-h-[48rem] text-sm font-mono m-0 border-1 border-gray-200">
                  {eventData.parsed}
                </pre>
              </div>
            )
          )}
        </Panel>
      )}

      {!log.contextProperties && !log.eventDataFileId && (
        <div className="text-center text-gray-500 py-6">
          <i className="pi pi-info-circle text-4xl mb-3" />
          <div>No additional details available for this event.</div>
        </div>
      )}
    </div>
  );
};

export default TraceLogDetailPanel;
